import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { CalendarBlank, ArrowRight, Buildings } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogListPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = 'Blog — HostCockpit | AI Property Management Insights for Airbnb Hosts';
    const m = document.querySelector('meta[name="description"]');
    if (m) m.setAttribute('content', 'Insights, guides, and reviews for short-term rental owners. Learn how to save thousands on property management, automate guest communication, and maximize Airbnb revenue.');
    axios.get(`${API}/blog`).then(r => setPosts(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="blog-list-page">
      <header className="border-b border-zinc-200 sticky top-0 bg-white z-40">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-7 h-7 bg-[#0052FF] rounded-sm flex items-center justify-center">
              <Buildings weight="bold" className="text-white" size={14} />
            </div>
            <span className="text-lg font-bold text-zinc-950 tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>HostCockpit</span>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/login" className="text-sm text-zinc-600 hover:text-zinc-900">Sign in</Link>
            <Link to="/register" className="px-4 py-2 bg-[#0052FF] text-white text-sm font-bold rounded-sm hover:bg-[#003EDD]" data-testid="blog-cta">Start Free Trial</Link>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-16">
        <div className="mb-14">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#0052FF] mb-4">The HostCockpit Blog</p>
          <h1 className="text-4xl sm:text-5xl font-bold text-zinc-950 tracking-tight mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
            Short-term rental insights for owners who actually run the numbers.
          </h1>
          <p className="text-lg text-zinc-600 max-w-2xl">
            Practical guides on automating guest communication, cutting property-management fees, and running Airbnbs that feel five-star — without hiring a manager.
          </p>
        </div>

        {loading ? (
          <p className="text-sm text-zinc-500">Loading articles…</p>
        ) : posts.length === 0 ? (
          <div className="border border-zinc-200 p-16 text-center bg-zinc-50">
            <p className="text-sm text-zinc-500">No posts yet. Check back soon.</p>
          </div>
        ) : (
          <div className="space-y-10" data-testid="blog-posts">
            {posts.map((p, i) => (
              <Link
                key={p.slug}
                to={`/blog/${p.slug}`}
                className="block group border-b border-zinc-100 pb-10 last:border-b-0"
                data-testid={`blog-card-${i}`}
              >
                <div className="flex items-center gap-3 mb-3">
                  <CalendarBlank size={12} className="text-zinc-400" />
                  <time className="text-xs text-zinc-500 font-medium">{p.published_at?.slice(0, 10)}</time>
                  <span className="text-xs text-zinc-400">·</span>
                  <span className="text-xs text-zinc-500">{p.author || 'HostCockpit Team'}</span>
                </div>
                <h2 className="text-2xl sm:text-3xl font-bold text-zinc-950 tracking-tight mb-3 group-hover:text-[#0052FF] transition-colors" style={{ fontFamily: 'var(--font-heading)' }}>
                  {p.title}
                </h2>
                <p className="text-base text-zinc-600 mb-4 max-w-2xl">{p.excerpt}</p>
                <span className="inline-flex items-center gap-1.5 text-sm font-bold text-[#0052FF]">
                  Read article <ArrowRight size={14} weight="bold" className="group-hover:translate-x-0.5 transition-transform" />
                </span>
              </Link>
            ))}
          </div>
        )}

        <div className="mt-20 p-8 border border-zinc-200 bg-gradient-to-br from-blue-50 to-white text-center">
          <h3 className="text-2xl font-bold text-zinc-950 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>Stop giving away 20-30% of your revenue.</h3>
          <p className="text-sm text-zinc-600 mb-5 max-w-lg mx-auto">HostCockpit replaces your Airbnb property manager with AI. Save $5K-$15K per property per year.</p>
          <Link to="/register" className="inline-block px-6 py-3 bg-[#0052FF] text-white text-sm font-bold rounded-sm hover:bg-[#003EDD]">Start Free Trial</Link>
        </div>
      </main>

      <footer className="border-t border-zinc-200 py-8 mt-8">
        <div className="max-w-6xl mx-auto px-6 flex flex-col md:flex-row justify-between text-xs text-zinc-500 gap-2">
          <p>© 2026 HostCockpit. All rights reserved.</p>
          <div className="flex gap-4">
            <Link to="/" className="hover:text-zinc-900">Home</Link>
            <Link to="/sms-consent" className="hover:text-zinc-900">SMS Terms</Link>
            <a href="mailto:hosts@hostcockpit.com" className="hover:text-zinc-900">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
